import { ref, computed } from 'vue'
import { getCookie, setCookie, removeCookie } from '../utils/cookies'

const COOKIE_CONSENT_KEY = 'bb_cookie_consent'
const COOKIE_PREFERENCES_KEY = 'bb_cookie_preferences'
const COOKIE_EXPIRY_DAYS = 365 // 1 year

// Cookie consent state
const consentGiven = ref(false)
const showConsentBanner = ref(false)

// Default cookie preferences
const defaultPreferences = {
  essential: true, // Always true, can't be disabled
  all: false
}

const cookiePreferences = ref({ ...defaultPreferences })

// Initialize on module load
function initializeCookieConsent () {
  try {
    // Check if consent has been given (prioritize cookie over localStorage)
    const cookieConsent = getCookie(COOKIE_CONSENT_KEY)
    const localStorageConsent = localStorage.getItem(COOKIE_CONSENT_KEY)

    consentGiven.value =
      cookieConsent === 'true' || localStorageConsent === 'true'

    // If we have localStorage but not cookie, migrate to cookie
    if (localStorageConsent === 'true' && !cookieConsent) {
      setCookie(COOKIE_CONSENT_KEY, 'true', COOKIE_EXPIRY_DAYS)
    }

    // Load saved preferences (prioritize cookie over localStorage)
    const cookiePrefs = getCookie(COOKIE_PREFERENCES_KEY)
    const localStoragePrefs = localStorage.getItem(COOKIE_PREFERENCES_KEY)

    if (cookiePrefs) {
      const parsed = JSON.parse(decodeURIComponent(cookiePrefs))
      cookiePreferences.value = { ...defaultPreferences, ...parsed }
    } else if (localStoragePrefs) {
      const parsed = JSON.parse(localStoragePrefs)
      cookiePreferences.value = { ...defaultPreferences, ...parsed }
      // Migrate to cookie
      setCookie(
        COOKIE_PREFERENCES_KEY,
        encodeURIComponent(JSON.stringify(cookiePreferences.value)),
        COOKIE_EXPIRY_DAYS
      )
    }

    // Show banner if consent hasn't been given
    showConsentBanner.value = !consentGiven.value
  } catch (error) {
    console.error('Error initializing cookie consent:', error)
    showConsentBanner.value = true
  }
}

export function useCookieConsent () {
  // Initialize if not already done
  if (
    !consentGiven.value &&
    localStorage.getItem(COOKIE_CONSENT_KEY) === 'true'
  ) {
    initializeCookieConsent()
  }

  const acceptAll = () => {
    cookiePreferences.value = {
      essential: true,
      all: true
    }
    savePreferences()
    consentGiven.value = true
    showConsentBanner.value = false
    // Store consent in both cookie and localStorage for redundancy
    setCookie(COOKIE_CONSENT_KEY, 'true', COOKIE_EXPIRY_DAYS)
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
  }

  const acceptEssential = () => {
    cookiePreferences.value = {
      essential: true,
      all: false
    }
    savePreferences()
    consentGiven.value = true
    showConsentBanner.value = false
    // Store consent in both cookie and localStorage for redundancy
    setCookie(COOKIE_CONSENT_KEY, 'true', COOKIE_EXPIRY_DAYS)
    localStorage.setItem(COOKIE_CONSENT_KEY, 'true')
  }

  const updatePreferences = preferences => {
    // Ensure essential is always true
    cookiePreferences.value = {
      ...preferences,
      essential: true
    }
    savePreferences()
  }

  const savePreferences = () => {
    try {
      const prefsString = JSON.stringify(cookiePreferences.value)
      // Store in both cookie and localStorage for redundancy
      setCookie(
        COOKIE_PREFERENCES_KEY,
        encodeURIComponent(prefsString),
        COOKIE_EXPIRY_DAYS
      )
      localStorage.setItem(COOKIE_PREFERENCES_KEY, prefsString)
    } catch (error) {
      console.error('Error saving cookie preferences:', error)
    }
  }

  const resetConsent = () => {
    // Remove from both cookie and localStorage
    removeCookie(COOKIE_CONSENT_KEY)
    removeCookie(COOKIE_PREFERENCES_KEY)
    localStorage.removeItem(COOKIE_CONSENT_KEY)
    localStorage.removeItem(COOKIE_PREFERENCES_KEY)
    consentGiven.value = false
    showConsentBanner.value = true
    cookiePreferences.value = { ...defaultPreferences }
  }

  // Check if a specific cookie category is allowed
  const canUseEssentialCookies = computed(
    () => cookiePreferences.value.essential
  )
  const canUseAllCookies = computed(() => cookiePreferences.value.all)

  return {
    consentGiven,
    showConsentBanner,
    cookiePreferences,
    acceptAll,
    acceptEssential,
    updatePreferences,
    savePreferences,
    resetConsent,
    canUseEssentialCookies,
    canUseAllCookies
  }
}

// Auto-initialize
initializeCookieConsent()
